// src/components/ui/MetricsSection.jsx
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  useTheme,
  alpha
} from '@mui/material';
import { BarChart as AnalyticsIcon } from '@mui/icons-material';
import MetricCard from './MetricCard';
import { format } from 'date-fns';

/**
 * Reusable metrics section component
 * @param {Object} props
 * @param {Object} props.metrics - The metrics object
 * @param {string} props.status - The content status
 * @param {string} props.contentId - ID of the content
 * @param {Function} props.onViewAnalytics - Function to call to view analytics
 */
const MetricsSection = ({ metrics, status, contentId, onViewAnalytics }) => {
  const theme = useTheme();
  
  const hasMetrics = status === 'published' && metrics && Object.keys(metrics).length > 0;
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM d, yyyy HH:mm');
  };
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Performance Metrics
      </Typography>
      
      {!hasMetrics ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="textSecondary" align="center">
            {status !== 'published' 
              ? 'Metrics will be available after the content is published.' 
              : 'No metrics data available yet.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard 
              title="Impressions" 
              value={metrics.totalImpressions || 0} 
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard 
              title="Reach" 
              value={metrics.totalReach || 0} 
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard 
              title="Engagement" 
              value={metrics.totalEngagement || 0} 
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard 
              title="Engagement Rate" 
              value={metrics.engagementRate || 0} 
              format="percentage"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard 
              title="Likes" 
              value={metrics.totalLikes || 0} 
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard 
              title="Comments" 
              value={metrics.totalComments || 0} 
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard 
              title="Shares" 
              value={metrics.totalShares || 0} 
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 1 }}>
              <Typography variant="caption" display="block" align="center">
                Last Updated: {metrics.lastUpdated 
                  ? formatDate(metrics.lastUpdated) 
                  : 'N/A'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}
      
      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onViewAnalytics}
          startIcon={<AnalyticsIcon />}
        >
          View Detailed Analytics
        </Button>
      </Box>
    </Box>
  );
};

export default MetricsSection;